import { Container, BitmapText, Text as PixiText, Text, TextStyle } from 'pixi.js'
import { preciseAdd, t, app, toCommaOptions, soundManager, sleep, language } from '@slot-nx/core'
import gsap from 'gsap'
import { Multiplier } from '../game/model'
import { spinService } from '../game'
import { ContainNameType } from '../game/enum/ContainNameType'
import { numberAnimationV2 } from '../game/util/gameUtil'
import { tableService } from '../table/tableService'
import { useGameStore } from '../game/store/GameStore'
import { Spine } from '@esotericsoftware/spine-pixi'
import { balancePanelTitleFontFamily } from '../../components/BalanceContainer'
import { GameSoundType } from '../sound/GameSoundType'

const winTextStyle = new TextStyle({
  fontSize: 36,
  lineHeight: 30,
  align: 'center',
  fill: '#fff',
  fontWeight: '900',
  fontFamily: 'Copperplate Gothic',
})

const tumbleBarTitleStyle = new TextStyle({
  fontFamily: balancePanelTitleFontFamily,
  fontSize: 28,
  fontWeight: '900',
  fill: '#f5e6b8',
  stroke: '#2a1810',
  strokeThickness: 5,
  lineJoin: 'round',
  align: 'center',
  letterSpacing: 1.2,
  wordWrap: true,
  wordWrapWidth: 880,
  breakWords: true,
  dropShadow: true,
  dropShadowBlur: 6,
  dropShadowDistance: 3,
  dropShadowColor: '#140804',
  dropShadowAngle: Math.PI / 2,
})

class WinningService {
  private _winBarContainer: Container
  private _tumbleWinBarContainer: Spine
  private _totalMultiplierContainer: Container // totalMultiplier 父層container
  private _totalMultiplierSpineBox: Spine // totalMultiplier 框框本身
  private _totalMultiplierText: BitmapText | null = null
  private _multiplierContainer: Container
  private _multiplierSpineBox: Spine
  private _spinTextRef: PixiText
  private _multiplierText: BitmapText | null = null
  private _tumbleWinText: BitmapText | null = null
  private _tumbleWinLabel?: Text
  private _tumbleBarTitleLabel?: Text
  private _tumbleBarTitleConfigured = false
  private _totalMultiplier = 0
  private _multiplierValue = 0
  private _multiplierPulseScale = 1.6
  private readonly _multiplierPulseStep = 0.15
  private readonly _multiplierPulseMax = 2.5
  private _tumbleWinAmount = 0
  private _winBoardAmount = 0
  public winTextRef: Container

  public setWinBarContainer(container: Container) { this._winBarContainer = container }
  public setTotalMultiplierContainer(container: Container) { this._totalMultiplierContainer = container }
  public setTotalMultiplierSpineBox(Spine: Spine) { this._totalMultiplierSpineBox = Spine }
  public setMultiplierContainer(container: Container) { this._multiplierContainer = container }
  public setMultiplierSpineBox(Spine: Spine) { this._multiplierSpineBox = Spine }
  public setTumbleWinBar(Spine: Spine) {
    this._tumbleWinBarContainer = Spine
    if (!this._tumbleBarTitleConfigured) {
      this._tumbleBarTitleConfigured = true
      this.configureTumbleBarTitle(Spine)
    }
  }

  /** 標題與 Spine 同層、僅版面疊放（不綁 slot）；Spine 圖檔需自行去掉內建字。 */
  private configureTumbleBarTitle(spine: Spine) {
    const parent = spine.parent
    if (!parent) return

    const title = new Text(t('winBar_tumbleWinTitle'), tumbleBarTitleStyle)
    title.name = 'tumbleBarTitle'
    title.anchor.set(0.5)
    title.position.set(0, -58)
    this._tumbleBarTitleLabel = title
    parent.addChild(title)
  }

  private refreshTumbleBarTitleCopy() {
    if (this._tumbleBarTitleLabel) this._tumbleBarTitleLabel.text = t('winBar_tumbleWinTitle')
  }

  public setSpinText(text: PixiText) { this._spinTextRef = text }
  public setWinTextContainer(container: Container) { this.winTextRef = container }

  public init() {
    this._totalMultiplier = 0
    this._multiplierValue = 0
    this._multiplierText = null
    this._multiplierPulseScale = 1.6
    this._winBoardAmount = 0
    this._tumbleWinAmount = 0
    this._tumbleWinText?.destroy()
    this._tumbleWinText = null
    this._totalMultiplierText?.destroy()
    this._totalMultiplierText = null
    this._tumbleWinLabel?.destroy()
    this._tumbleWinLabel = undefined
    this.refreshTumbleBarTitleCopy()
    this.showGoodLuckText()
    useGameStore.setState({ dropIndex: 0 })
    tableService.resetUpperReelsNode()
    // tableService.generateUpperReelsNode() // todo forget why need to do, have a bug
  }

  public initFreeGame() {
    this._multiplierValue = 0
    this._multiplierText = null
    this._multiplierPulseScale = 1.6
    this._tumbleWinAmount = 0
    this._tumbleWinText?.destroy()
    this._tumbleWinText = null
    this._tumbleWinLabel?.destroy()
    this._tumbleWinLabel = undefined
    this.refreshTumbleBarTitleCopy()
    useGameStore.setState({ dropIndex: 0 })
    tableService.resetUpperReelsNode()
  }

  public showGoodLuckText() {
    this.winTextRef.alpha = 0
    this._spinTextRef.text = t('winBar_GoodLuck')
    this._spinTextRef.alpha = 1
    const tl = gsap.getTweensOf(this._spinTextRef)
    tl.forEach(tween => tween.kill())
  }

  public showSpinToWinText() {
    this.winTextRef.alpha = 0
    this._spinTextRef.text = t('winBar_SpinToWin')
    gsap.timeline({ repeat: -1 })
      .set(this._spinTextRef, { alpha: 1 })
      .to(this._spinTextRef, { duration: 0.5, alpha: 1 })
      .to(this._spinTextRef, { duration: 0.5, alpha: 0 })
      .to(this._spinTextRef, { duration: 0.5, alpha: 0 })
      .to(this._spinTextRef, { duration: 0.5, alpha: 1 })
  }

  public async updateWinBoard(payout: number) {
    this.winTextRef.alpha = 1
    this._spinTextRef.alpha = 0
    this.winTextRef.visible = true
    const text = this.winTextRef.getChildByName('winBarAmount') as Text
    const startValue = this._winBoardAmount * 100
    const endValue = preciseAdd(this._winBoardAmount, payout)
    this.updateWinLabel(endValue)
    const [task] = numberAnimationV2(startValue, endValue, 0.4, text, toCommaOptions.forceTwoDecimalPlaces)
    await task
    this._winBoardAmount = endValue
  }

  /**
   * 派彩未超過本次注額時，win 相關文字不能顯示「得分」這類慶祝字樣，改用中性的「返還」。
   * 只比較主遊戲的本局累計派彩與實際下注金額——免費遊戲本身已由觸發時的注額支付，
   * 不適用這條規則。WinBoard 的 win/return 文字與 WinBar 的 tumble win 文字都要用同一套判斷，
   * 否則玩家會同時看到一個正確顯示 Return、另一個仍寫死顯示 Win。
   */
  private computeIsWin(totalPayout: number): boolean {
    const { lastPayStake, isFreeGame } = useGameStore.getState()
    return isFreeGame || totalPayout > lastPayStake
  }

  private updateWinLabel(totalPayout: number) {
    // WinBoard 的 "win" Text 是 JSX 宣告、text prop 寫死 main_control_panel_win，
    // 直接 imperative 改 .text 會在 WinBoard 下次重新渲染時被 JSX 預設值蓋回去——
    // 改寫進 store，讓 WinBoard 用 isMainGameWin 反應式渲染 text prop 才會穩定生效。
    useGameStore.setState({ isMainGameWin: this.computeIsWin(totalPayout) })
  }

  public async updateTotalMultiplier() {
    const { spinMultiplier, totalMultiplier } = useGameStore.getState().getGameResult()
    const container = this._totalMultiplierContainer
    container.visible = true
    if (!this._totalMultiplierText) {
      this._totalMultiplierText = new BitmapText(`${spinMultiplier}X`, { fontName: 'fontB', fontSize: 28, align: 'center' })
      this._totalMultiplierText.anchor.set(0.5)
      this._totalMultiplierText.position.set(-70, -20)
      container.addChild(this._totalMultiplierText)
    }

    const [task] = numberAnimationV2(spinMultiplier, totalMultiplier, 0.2, this._totalMultiplierText, { maximumFractionDigits: 0, minimumFractionDigits: 0 }, 'X')
    this._totalMultiplier = totalMultiplier
    await task
  }

  public async updateMultiplier(startValue: number, endValue: number) {
    const container = this._multiplierContainer
    if (!this._multiplierText) {
      this._multiplierText = new BitmapText('', { fontName: 'fontB', fontSize: 20, align: 'center' })
      this._multiplierText.anchor.set(0.5)
      this._multiplierText.position.set(0, -10)
      container.addChild(this._multiplierText)
    }
    this._multiplierText.text = `${endValue}X`
    const multiplierScale = this._multiplierText.scale
    const pulseScale = this._multiplierPulseScale
    this._multiplierPulseScale = Math.min(this._multiplierPulseScale + this._multiplierPulseStep, this._multiplierPulseMax)
    gsap.killTweensOf(multiplierScale)
    multiplierScale.set(1)

    const scaleTask = new Promise<void>((resolve) => {
      gsap.to(multiplierScale, {
        x: pulseScale,
        y: pulseScale,
        duration: 0.1,
        repeat: 1,
        yoyo: true,
        ease: 'power2.out',
        onComplete: () => resolve(),
      })
    })

    await Promise.all([scaleTask])
  }

  public async collectMultiplier(list: Multiplier[]) {
    await this.showMultiplierContainer(true)
    const animationTasks: Promise<void>[] = []

    for (const [index, item] of list.entries()) {
      const { x, y } = item.position
      const symbolContainer = spinService.reelNodes[x]?.children[y] as Container
      const multiplierText = symbolContainer.getChildByName<BitmapText>(ContainNameType.SymbolMulpitlierNumber) as BitmapText
      const originalScaleX = multiplierText.scale.x
      const originalScaleY = multiplierText.scale.y
      const startPos = multiplierText.getGlobalPosition()
      const endPos = this._multiplierContainer.getGlobalPosition()
      app.stage.addChild(multiplierText)
      gsap.killTweensOf(multiplierText.scale)
      multiplierText.position.set(startPos.x, startPos.y - 5)
      multiplierText.scale.set(0.75) // 初始縮放
      gsap.to(multiplierText.scale, { x: 1.2, y: 1.2, duration: 0.4, ease: 'power2.out' })
      const animationTask = new Promise<void>((resolve) => {
        const multiplierSpineBox = this._multiplierSpineBox as Spine
        let isShow = false
        const timelineDelay = index * 0.5
        const totalDuration = 0.5
        const t1 = totalDuration * 0.1
        const t2 = totalDuration * 0.35
        const t3 = totalDuration * 0.15

        const tl = gsap.timeline({
          delay: timelineDelay,
          onStart: () => {
            soundManager.play(GameSoundType.ThreeComboOlympus_MultiplyValue)
            tableService.playSymbolSpine(x, y, 'win', 'mulpitlierWin', item)
          },
          onUpdate: () => {
            const t = tl.progress()
            if (t >= 0.8 && !isShow) {
              isShow = true
              multiplierSpineBox.state.setAnimation(0, 'win', false)
              multiplierSpineBox.state.addAnimation(0, 'loop', true, 0)
            }
          },
          onComplete: () => {
            symbolContainer.addChild(multiplierText)
            multiplierText.position.set(76.5, 52)
            multiplierText.scale.set(originalScaleX, originalScaleY)
            setTimeout(async () => {
              const startValue = this._multiplierValue
              const endValue = preciseAdd(startValue, item.multiplier, 0)
              this._multiplierValue = endValue
              await this.updateMultiplier(startValue, endValue)
              resolve()
            }, 50)
          },
        })
        tl.to(multiplierText, { x: endPos.x - 10, y: endPos.y - 10, duration: totalDuration, ease: 'power2.inOut' }, 0)
        tl.to(multiplierText.scale, { x: 1.5, y: 1.5, duration: t2, ease: 'power2.inOut' }, t1)
        tl.to(multiplierText.scale, { x: 0.8, y: 0.8, duration: t3, ease: 'power2.inOut' }, t1 + t2)
      })
      animationTasks.push(animationTask)
    }

    await Promise.all(animationTasks)
  }

  public async showMultiplierContainer(show: boolean) {
    const alpha = show ? 1 : 0
    gsap.to(this._multiplierContainer, { alpha: alpha, duration: 0.3, ease: 'power1.inOut' })
  }

  public showTumblewinText(totalPayout: number) {
    const isViVn = language === 'VI_VN'
    const baseWinTextStyle = winTextStyle.clone()
    const tumbleLabelOffsetY = isViVn ? -20 : 0
    if (isViVn) {
      baseWinTextStyle.fontSize = 20
    }
    const container = this._winBarContainer
    this.refreshTumbleBarTitleCopy()
    const winLabel = this.computeIsWin(totalPayout) ? t('main_control_panel_win') : t('main_control_panel_return')
    this._tumbleWinLabel = new Text(winLabel, baseWinTextStyle)
    this._tumbleWinLabel.name = 'win'
    this._tumbleWinLabel.anchor.set(0.5)
    this._tumbleWinLabel.position.set(-200 + tumbleLabelOffsetY, 14)
    container.addChild(this._tumbleWinLabel)
  }

  public async updateTumbleWin(amount: number, showTotal = false, showScale = false) {
    const container = this._winBarContainer

    /** ⭐ 建立金額文字（只做一次） */
    if (!this._tumbleWinText) {
      this._tumbleWinText = new BitmapText('0', { fontName: 'fontD', fontSize: 14, align: 'center' })
      this._tumbleWinText.anchor.set(0.5)
      this._tumbleWinText.position.set(0, 0)
      container.addChild(this._tumbleWinText)
      this._tumbleWinText.y += 4
    }
    const tumbleWinText = this._tumbleWinText

    const currentValue = this._tumbleWinAmount
    const endValue = showTotal ? amount : preciseAdd(currentValue, amount)
    const [task] = numberAnimationV2(currentValue, endValue, 0.3, tumbleWinText, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })
    if (showScale) {
      gsap.killTweensOf(tumbleWinText.scale)
      tumbleWinText.scale.set(1)
      const scaleTask = new Promise<void>((resolve) => {
        gsap.to(tumbleWinText.scale, {
          x: 1.8,
          y: 1.8,
          duration: 0.2,
          ease: 'power2.out',
          yoyo: true,
          repeat: 1,
          onComplete: resolve,
        })
      })
      await Promise.all([task, scaleTask])
    }
    else {
      await task
    }
    this._tumbleWinAmount = endValue
  }

  public tumbleWin() {
    this._tumbleWinBarContainer.state.setAnimation(0, 'win', false)
    this._tumbleWinBarContainer.state.addAnimation(0, 'loop', true)
  }

  public async payoutMultiplier(totalPayout: number) {
    const container = this._multiplierText as BitmapText
    soundManager.play(GameSoundType.ThreeComboOlympus_MultiplyTOTumble)
    await new Promise<void>((resolve) => {
      gsap.to(container, {
        x: -270,
        y: -10,
        duration: 0.5,
        ease: 'power2.in',
        onComplete: () => {
          gsap.to(container.scale, {
            x: 1.5,
            y: 1.5,
            duration: 0.2,
            ease: 'power2.out',
            yoyo: true,
            repeat: 1,
            onComplete: () => {
              (async () => {
                this._multiplierText?.destroy()
                this.showMultiplierContainer(false)
                this.tumbleWin()
                await winningService.updateTumbleWin(totalPayout, true)
                resolve()
              })()
            },
          })
        },
      })
    })
  }

  // bonusGame totalMultiper 收集到右側單次spin multiplier上
  public async bgPayoutMultiplier(totalPayout: number) {
    const container = this._multiplierText as BitmapText
    soundManager.play(GameSoundType.ThreeComboOlympus_MultiplyTOTumble)
    await new Promise<void>((resolve) => {
      gsap.to(container, {
        x: -270,
        y: -10,
        duration: 0.5,
        ease: 'power2.in',
        onComplete: () => {
          gsap.to(container.scale, {
            x: 1.5,
            y: 1.5,
            duration: 0.2,
            ease: 'power2.out',
            yoyo: true,
            repeat: 1,
            onComplete: () => {
              void (async () => {
                this._multiplierText?.destroy()
                this.showMultiplierContainer(false)
                this.tumbleWin()
                await winningService.updateTumbleWin(totalPayout, true, true)
                await sleep(350)
                await winningService.updateTotalMultiplier()
                resolve()
              })()
            },
          })
        },
      })
    })
  }

  public async collectTotalMultiplier() {
    if (!this._totalMultiplierText && !this._totalMultiplier) return
    const text = new BitmapText(`${this._totalMultiplier}X`, { fontName: 'fontB', fontSize: 24, align: 'center' })

    await this.showMultiplierContainer(true)
    soundManager.play(GameSoundType.ThreeComboOlympus_TotalMultiply)
    await new Promise<void>((resolve) => {
      const startPos = this._totalMultiplierContainer.getGlobalPosition()
      const endPos = this._multiplierContainer.getGlobalPosition()
      text.anchor.set(0.5)
      text.position.set(startPos.x, startPos.y)
      text.scale.set(0.75) // 初始縮放
      app.stage.addChild(text)

      const multiplierSpineBox = this._multiplierSpineBox as Spine
      let isShow = false
      const totalDuration = 0.5
      const t1 = totalDuration * 0.1
      const t2 = totalDuration * 0.35
      const t3 = totalDuration * 0.05

      const tl = gsap.timeline({
        onUpdate: () => {
          const t = tl.progress()
          if (t >= 0.8 && !isShow) {
            isShow = true
            multiplierSpineBox.state.setAnimation(0, 'win', false)
            multiplierSpineBox.state.addAnimation(0, 'loop', true, 0)
          }
        },
        onComplete: () => {
          resolve()
        },
      })

      // 位移整段動畫
      tl.to(text, { x: endPos.x - 10, y: endPos.y - 10, duration: totalDuration, ease: 'power2.inOut' }, 0)
      tl.to(text.scale, { x: 1.1, y: 1.1, duration: t2, ease: 'power2.inOut' }, t1)
      tl.to(text.scale, { x: 0.6, y: 0.6, duration: t3, ease: 'power2.inOut' }, t1 + t2)
      tl.to(text, { alpha: 0.75, scale: 0.7, duration: 0.1, ease: 'power1.in' }, totalDuration - 0.1)
    })

    // 更新 multiplier 數值
    const endValue = this._totalMultiplier
    await this.updateMultiplier(endValue, endValue)
    this._multiplierValue = endValue
    text.destroy()
  }
}

export const winningService = new WinningService()
